import { useState, useEffect } from 'react'
import { createResponseLink, createCardLink } from '../services/linkService'
import { getResponseTypeConfig } from '../data/topics/index.js'
import { saveSenderResponses, saveRecipientResponses, markCardAsOpened, CARD_STATUS, createCardWithStatus } from '../services/cardStatusService'
import InvitationCard from './InvitationCard'
import '../styles/components/response-form.css'

// Import sessionStorage for checking sender response
const isSenderResponse = () => {
  if (typeof window === 'undefined') return false
  return sessionStorage.getItem('parkort_is_sender_response') === 'true'
}

function ResponseFormV2({ cardData, onComplete, isSenderResponse: propIsSenderResponse = false }) {
  const [responses, setResponses] = useState({})
  const [currentIndex, setCurrentIndex] = useState(0)
  const [bookingResponse, setBookingResponse] = useState(null)
  const [alternativeBooking, setAlternativeBooking] = useState(null)
  const [showAltBooking, setShowAltBooking] = useState(false)
  const [showInvitation, setShowInvitation] = useState(true)
  const [invitationAccepted, setInvitationAccepted] = useState(false)

  useEffect(() => {
    if (cardData && cardData.preferences) {
      const initialResponses = {}
      cardData.preferences.forEach((pref, index) => {
        const id = pref.id || index
        initialResponses[id] = null
      })
      setResponses(initialResponses)
      
      // Reset currentIndex if it's out of bounds
      if (currentIndex >= cardData.preferences.length) {
        setCurrentIndex(0)
      }
    }
    
    // Mark card as opened if this is recipient (not sender)
    if (cardData && !propIsSenderResponse && !isSenderResponse()) {
      const shortCode = cardData.shortCode || (cardData.id && cardData.id.length === 8 ? cardData.id : null)
      if (shortCode) {
        markCardAsOpened(shortCode).catch(error => {
          console.error('Error marking card as opened:', error)
        })
      }
    }
  }, [cardData, propIsSenderResponse])

  const setResponse = (itemId, response) => {
    setResponses(prev => ({
      ...prev,
      [itemId]: response
    }))
    
    // Auto-advance to next question after a short delay
    setTimeout(() => {
      setCurrentIndex(prev => {
        const maxIndex = (cardData?.preferences?.length || 0) - 1
        if (prev < maxIndex) {
          return prev + 1
        }
        // Don't advance if already at last question
        return prev
      })
    }, 500) // Increased delay to ensure state is updated
  }

  const getResponseType = (pref) => {
    return pref.responseType || 'YNM'
  }

  const getResponseButtons = (responseType) => {
    const config = getResponseTypeConfig(responseType)
    if (!config) {
      // Fallback to YNM
      return [
        { value: 'yes', label: '✅ Ja' },
        { value: 'maybe', label: '🤔 Måske' },
        { value: 'no', label: '❌ Nej' }
      ]
    }

    return Object.entries(config.labels).map(([value, label]) => ({
      value,
      label
    }))
  }

  const handleBookingResponse = (response) => {
    setBookingResponse(response)
    if (response === 'alternative') {
      setShowAltBooking(true)
    }
  }

  const setAltQuickTime = (option) => {
    const now = new Date()
    let targetDate = new Date()
    
    switch (option) {
      case 'tonight':
        targetDate.setHours(20, 0, 0, 0)
        break
      case 'tomorrow':
        targetDate.setDate(now.getDate() + 1)
        targetDate.setHours(20, 0, 0, 0)
        break
      case 'weekend':
        const daysUntilSaturday = (6 - now.getDay()) % 7 || 7
        targetDate.setDate(now.getDate() + daysUntilSaturday)
        targetDate.setHours(18, 0, 0, 0)
        break
      case 'custom':
        return
    }
    
    setAlternativeBooking({
      date: targetDate.toISOString().split('T')[0],
      time: targetDate.toTimeString().slice(0, 5)
    })
  }

  const handleSubmit = async () => {
    const allResponsed = Object.values(responses).every(r => r !== null && r !== undefined)
    if (!allResponsed) {
      // Show friendly error message
      const unansweredCount = Object.values(responses).filter(r => r === null || r === undefined).length
      
      const errorMsg = document.createElement('div')
      errorMsg.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(255, 107, 107, 0.95);
        color: white;
        padding: 16px 24px;
        border-radius: 12px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        z-index: 10000;
        animation: slideDown 0.3s ease-out;
      `
      errorMsg.textContent = `⚠️ Du mangler at svare på ${unansweredCount} spørgsmål. Scroll op og besvar dem først.`
      document.body.appendChild(errorMsg)
      setTimeout(() => {
        errorMsg.style.animation = 'fadeOut 0.3s ease-out'
        setTimeout(() => errorMsg.remove(), 300)
      }, 4000)
      return
    }

    // Check if this is sender responding
    const isSender = propIsSenderResponse || isSenderResponse()
    
    if (isSender) {
      // Store sender responses and preferences/booking for returning to share link
      sessionStorage.setItem('parkort_sender_responses', JSON.stringify(responses))
      sessionStorage.setItem('parkort_sender_booking_response', JSON.stringify({ bookingResponse, alternativeBooking }))
      sessionStorage.setItem('parkort_sender_has_responded', 'true')
      sessionStorage.removeItem('parkort_is_sender_response')
      
      // Store preferences and booking so we can return to share link
      if (cardData.preferences) {
        sessionStorage.setItem('parkort_sender_preferences', JSON.stringify(cardData.preferences))
      }
      if (cardData.booking) {
        sessionStorage.setItem('parkort_sender_booking', JSON.stringify(cardData.booking))
      }
      
      // Call onComplete to go to share step (no shortCode needed - we use long links)
      onComplete({ 
        responses, 
        bookingResponse, 
        alternativeBooking
      })
      return
    }
    
    // This is recipient responding - mark card as opened first
    const shortCode = cardData.shortCode || (cardData.id && cardData.id.length === 8 ? cardData.id : null)
    if (shortCode) {
      try {
        await markCardAsOpened(shortCode)
      } catch (error) {
        console.error('Error marking card as opened:', error)
      }
    }
    
    try {
      const cardIdentifier = cardData.shortCode || cardData.id || 'temp'
      
      // Save recipient responses to Firebase if we have shortCode
      if (shortCode) {
        try {
          await saveRecipientResponses(shortCode, responses, bookingResponse, alternativeBooking)
        } catch (error) {
          console.error('Error saving recipient responses to Firebase:', error)
        }
      }
      
      const responseLink = await createResponseLink({
        cardId: cardIdentifier,
        shortCode: cardData.shortCode || shortCode,
        preferences: cardData.preferences,
        responses,
        bookingResponse,
        alternativeBooking
      })
      
      window.responseLink = responseLink
      onComplete({ 
        responses, 
        bookingResponse, 
        alternativeBooking,
        responseLink 
      })
    } catch (error) {
      console.error('Error creating response link:', error)
      const data = encodeURIComponent(JSON.stringify({
        preferences: cardData.preferences,
        responses,
        booking: cardData.booking,
        bookingResponse,
        alternativeBooking
      }))
      window.responseLink = `${window.location.origin}/?results=${data}`
      onComplete({ responses, bookingResponse, alternativeBooking })
    }
  }

  if (!cardData || !cardData.preferences || cardData.preferences.length === 0) {
    return (
      <div className="loading-state" style={{
        textAlign: 'center',
        padding: '60px 20px'
      }}>
        <div className="loading-spinner" style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(102, 126, 234, 0.2)',
          borderTop: '4px solid var(--cyber-blue)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 20px'
        }}></div>
        <h3>Indlæser kort...</h3>
        <p style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>
          Henter spørgsmålene
        </p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    )
  }

  const totalQuestions = cardData.preferences.length
  
  // Ensure currentIndex is within bounds
  const safeIndex = Math.min(Math.max(0, currentIndex), totalQuestions - 1)
  const currentPref = cardData.preferences[safeIndex]
  
  // Get topic data and instruction text
  // Try multiple sources: cardData.topicData (from loaded topics) or window.currentTopicData
  let topicData = null
  let instructionText = null
  
  // Source 1: From cardData.topicData (when topicId is available)
  if (currentPref?.topicId && cardData.topicData?.[currentPref.topicId]) {
    topicData = cardData.topicData[currentPref.topicId]
    instructionText = topicData.instructionText
  }
  
  // Source 2: From window.currentTopicData (set by PreferenceSelector)
  if (!instructionText && window.currentTopicData) {
    instructionText = window.currentTopicData.instructionText
  }
  
  if (!currentPref) {
    return (
      <div className="response-form step active" style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: '3em', marginBottom: '16px' }}>⚠️</div>
        <h2>Spørgsmål ikke fundet</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
          Der opstod et problem med at indlæse spørgsmålet. Prøv at opdatere siden.
        </p>
        <button className="btn" onClick={() => window.location.reload()}>
          Opdater siden
        </button>
      </div>
    )
  }
  
  const itemId = currentPref.id || safeIndex
  const responseType = getResponseType(currentPref)
  const buttons = getResponseButtons(responseType)
  const currentResponse = responses[itemId]
  const allResponsed = Object.values(responses).every(r => r !== null && r !== undefined)
  const answeredCount = Object.values(responses).filter(r => r !== null && r !== undefined).length
  const progress = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0

  // Show booking section after all questions are answered (only if booking exists)
  const showBooking = allResponsed && cardData.booking
  // Show submit button if all answered but no booking
  const showSubmit = allResponsed && !cardData.booking

  // Check if this is sender responding - prioritize prop over sessionStorage
  const senderIsResponding = propIsSenderResponse || isSenderResponse()
  
  // Show invitation card ONLY for recipients (NOT sender)
  if (!senderIsResponding && !invitationAccepted && showInvitation) {
    const handleInvitationAccept = (response) => {
      setInvitationAccepted(true)
      setShowInvitation(false)
    }
    
    return (
      <InvitationCard
        senderName={cardData?.senderName || "Nogen"}
        topics={cardData?.preferences || []}
        onAccept={handleInvitationAccept}
      />
    )
  }
  
  return (
    <div className="response-form step active">
      {/* Sender Response Notice */}
      {senderIsResponding && (
        <div style={{
          marginBottom: '24px',
          padding: '16px',
          background: 'rgba(102, 126, 234, 0.1)',
          borderRadius: '12px',
          border: '1px solid rgba(102, 126, 234, 0.3)'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '4px', color: 'var(--cyber-blue)' }}>
            💡 Du svarer på dit eget kort
          </div>
          <p style={{ margin: 0, fontSize: '0.9em', color: 'var(--text-secondary)' }}>
            Når modtageren også svarer, sammenligner vi jeres svar og giver jer begge match score og konkret feedback.
          </p>
        </div>
      )}
      
      {showBooking ? (
        <>
          {/* Booking Section */}
          <div className="booking-response">
            <h3>📅 Hvad med tidspunktet?</h3>
            <p>
              {new Date(`${cardData.booking.date}T${cardData.booking.time}`).toLocaleDateString('da-DK', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
            
            <div className="booking-response-options">
              <button
                className={`booking-response-btn ${bookingResponse === 'accept' ? 'active' : ''}`}
                onClick={() => handleBookingResponse('accept')}
                aria-label="Accepter foreslået tidspunkt"
                aria-pressed={bookingResponse === 'accept'}
              >
                ✅ Perfekt timing!
              </button>
              <button
                className={`booking-response-btn ${bookingResponse === 'alternative' ? 'active' : ''}`}
                onClick={() => handleBookingResponse('alternative')}
                aria-label="Foreslå alternativt tidspunkt"
                aria-pressed={bookingResponse === 'alternative'}
              >
                📅 Foreslå anden tid
              </button>
            </div>
            
            {showAltBooking && (
              <div className="alternative-booking">
                <h4>Hvornår passer det dig bedre?</h4>
                <div className="quick-time-options">
                  <div className="quick-time-btn" onClick={() => setAltQuickTime('tonight')}>
                    🌙 I aften
                  </div>
                  <div className="quick-time-btn" onClick={() => setAltQuickTime('tomorrow')}>
                    ☀️ I morgen
                  </div>
                  <div className="quick-time-btn" onClick={() => setAltQuickTime('weekend')}>
                    🍷 På weekend
                  </div>
                  <div className="quick-time-btn" onClick={() => setAltQuickTime('custom')}>
                    📅 Vælg selv
                  </div>
                </div>
                
                {alternativeBooking && (
                  <div className="booking-preview">
                    <h3>📅 Dit alternativ:</h3>
                    <p>
                      {new Date(`${alternativeBooking.date}T${alternativeBooking.time}`).toLocaleDateString('da-DK', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                )}
              </div>
            )}

            <button 
              className="btn" 
              onClick={handleSubmit}
              disabled={!bookingResponse}
              aria-label="Send mine svar"
              aria-disabled={!bookingResponse}
              style={{ 
                marginTop: '24px',
                minHeight: '48px',
                width: '100%'
              }}
            >
              💌 Send mine svar
            </button>
          </div>
        </>
      ) : (
        <>
          <h2>Svar på spørgsmålene</h2>
          
          {/* Value Reminder - Enhanced */}
          <div style={{
            marginBottom: '24px',
            padding: '20px',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
            borderRadius: '16px',
            border: '2px solid rgba(102, 126, 234, 0.3)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2.5em', marginBottom: '12px' }}>💬</div>
            <div style={{ fontWeight: '700', marginBottom: '8px', color: 'var(--cyber-blue)', fontSize: '1.2em' }}>
              {instructionText || 'Svar ærligt - det giver bedre match!'}
            </div>
            <p style={{ margin: '0 0 16px 0', color: 'var(--text-primary)', lineHeight: '1.7', fontSize: '1em' }}>
              {senderIsResponding 
                ? 'Du svarer på dit eget kort. Når modtageren også svarer, sammenligner systemet jeres svar og giver jer begge match score og konkret feedback.'
                : 'Jo ærligere du svarer, jo bedre match score og feedback får I begge. Systemet sammenligner jeres svar og viser hvor I er enige og uenige.'}
            </p>
            {!senderIsResponding && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                padding: '12px',
                marginTop: '12px',
                fontSize: '0.9em',
                textAlign: 'left'
              }}>
                <strong style={{ display: 'block', marginBottom: '8px' }}>💡 Hvad sker der når du svarer?</strong>
                <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.6' }}>
                  <li>Systemet gemmer dine svar</li>
                  <li>Når modtageren også svarer, sammenlignes jeres svar</li>
                  <li>I får begge match score og konkret feedback</li>
                </ul>
              </div>
            )}
          </div>
          
          {/* Progress Bar */}
          <div className="progress-container">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <div className="progress-text">
              {answeredCount} / {totalQuestions}
            </div>
          </div>

          {/* Current Question */}
          <div className="question-card">
            <div className="question-number">
              Spørgsmål {safeIndex + 1} af {totalQuestions}
            </div>
            <div className="response-question">
              {currentPref.text || currentPref}
            </div>
            {/* Help Text / Explanation */}
            {(currentPref.helpText || currentPref.explanation) && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                background: 'rgba(102, 126, 234, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(102, 126, 234, 0.2)',
                fontSize: '0.9em',
                color: 'var(--text-secondary)',
                lineHeight: '1.6'
              }}>
                <strong style={{ color: 'var(--cyber-blue)', display: 'block', marginBottom: '4px' }}>
                  💡 Hvad betyder dette?
                </strong>
                {currentPref.helpText || currentPref.explanation}
              </div>
            )}
            
            <div className="response-buttons">
              {buttons.map((btn, btnIndex) => {
                const isActive = currentResponse === btn.value
                let buttonClass = 'response-btn'
                
                // Add type-specific classes
                if (responseType === 'YNM') {
                  if (btn.value === 'yes') buttonClass += ' yes'
                  else if (btn.value === 'maybe') buttonClass += ' maybe'
                  else if (btn.value === 'no') buttonClass += ' no'
                } else {
                  // For IMPORTANCE_0_3
                  const numValue = parseInt(btn.value)
                  if (!isNaN(numValue)) {
                    if (numValue >= 2) buttonClass += ' yes'
                    else if (numValue === 1) buttonClass += ' maybe'
                    else buttonClass += ' no'
                  }
                }
                
                return (
                  <button
                    key={btnIndex}
                    className={`${buttonClass} ${isActive ? 'active' : ''}`}
                    onClick={() => setResponse(itemId, btn.value)}
                    aria-label={`Svar: ${btn.label}`}
                    aria-pressed={isActive}
                  >
                    {btn.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Navigation */}
          <div className="question-navigation">
            <button
              className="nav-btn"
              onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
              disabled={safeIndex === 0}
              aria-label="Gå til forrige spørgsmål"
              aria-disabled={safeIndex === 0}
            >
              ← Forrige
            </button>
            
            <div className="question-dots">
              {cardData.preferences.map((pref, index) => {
                const prefId = pref.id || index
                const isAnswered = responses[prefId] !== null && responses[prefId] !== undefined
                return (
                  <div
                    key={index}
                    className={`dot ${index === safeIndex ? 'active' : ''} ${isAnswered ? 'answered' : ''}`}
                    onClick={() => setCurrentIndex(index)}
                    title={`Spørgsmål ${index + 1}${isAnswered ? ' (besvaret)' : ''}`}
                  />
                )
              })}
            </div>
            
            <button
              className="nav-btn"
              onClick={() => {
                if (safeIndex >= totalQuestions - 1 && allResponsed) {
                  // If at last question and all answered, scroll to submit button
                  const submitBtn = document.querySelector('.submit-all-btn')
                  if (submitBtn) {
                    submitBtn.scrollIntoView({ behavior: 'smooth', block: 'center' })
                  }
                } else {
                  setCurrentIndex(prev => Math.min(totalQuestions - 1, prev + 1))
                }
              }}
              disabled={safeIndex >= totalQuestions - 1 && !allResponsed}
              aria-label={safeIndex >= totalQuestions - 1 && allResponsed ? 'Alle spørgsmål besvaret' : 'Gå til næste spørgsmål'}
              aria-disabled={safeIndex >= totalQuestions - 1 && !allResponsed}
              style={{
                opacity: (safeIndex >= totalQuestions - 1 && !allResponsed) ? 0.5 : 1
              }}
            >
              {safeIndex >= totalQuestions - 1 && allResponsed ? '✓ Alle besvaret' : 'Næste →'}
            </button>
          </div>

          {/* Submit Button - Show when all answered but no booking */}
          {showSubmit && (
            <div className="submit-all-btn" style={{ marginTop: '32px', textAlign: 'center', padding: '24px' }}>
              <div style={{ 
                background: 'rgba(102, 126, 234, 0.1)', 
                borderRadius: '12px', 
                padding: '20px',
                marginBottom: '20px'
              }}>
                <h3 style={{ marginBottom: '8px' }}>✅ Alle spørgsmål besvaret!</h3>
                <p style={{ fontSize: '0.9em', opacity: 0.8, margin: 0 }}>
                  Du kan nu sende dine svar til afsenderen
                </p>
              </div>
            <button 
              className="btn" 
              onClick={handleSubmit}
              aria-label="Send mine svar"
              style={{
                fontSize: '1.2em',
                padding: '16px 32px',
                minHeight: '48px',
                fontWeight: 'bold',
                width: '100%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                  minWidth: '200px'
                }}
              >
                💌 Send mine svar
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ResponseFormV2
